import { gql, type TypedDocumentNode } from '@apollo/client';

import type {
  CompanyInvitation,
  CompanyProfile,
  CompanySettings,
  CreateCompanyInvitationInput,
  UpdateCompanyProfileInput,
  UpdateCompanySettingsInput,
  UserError,
} from '../types/companyPortal.types';
import { COMPANY_INVITATION_FIELDS, COMPANY_PROFILE_FIELDS } from './fragments';

interface InvitationPayload {
  companyInvitation: CompanyInvitation | null;
  errors: UserError[];
}

export const CREATE_COMPANY_INVITATION: TypedDocumentNode<
  { createCompanyInvitation: InvitationPayload },
  { input: CreateCompanyInvitationInput }
> = gql`
  mutation CreateCompanyInvitation($input: CreateCompanyInvitationInput!) {
    createCompanyInvitation(input: $input) {
      companyInvitation {
        ...CompanyInvitationFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

export const RESEND_COMPANY_INVITATION: TypedDocumentNode<
  { resendCompanyInvitation: InvitationPayload },
  { input: { invitationId: string } }
> = gql`
  mutation ResendCompanyInvitation($input: ResendCompanyInvitationInput!) {
    resendCompanyInvitation(input: $input) {
      companyInvitation {
        ...CompanyInvitationFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

export const CANCEL_COMPANY_INVITATION: TypedDocumentNode<
  { cancelCompanyInvitation: InvitationPayload },
  { input: { invitationId: string } }
> = gql`
  mutation CancelCompanyInvitation($input: CancelCompanyInvitationInput!) {
    cancelCompanyInvitation(input: $input) {
      companyInvitation {
        ...CompanyInvitationFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

/** Redeems the raw token: creates + activates the CompanyUser membership in one transaction. */
export const ACCEPT_COMPANY_INVITATION: TypedDocumentNode<
  {
    acceptCompanyInvitation: {
      companyUser: { id: string; companyId: string; role: string; status: string } | null;
      errors: UserError[];
    };
  },
  { input: { token: string } }
> = gql`
  mutation AcceptCompanyInvitation($input: AcceptCompanyInvitationInput!) {
    acceptCompanyInvitation(input: $input) {
      companyUser {
        id
        companyId
        role
        status
      }
      errors {
        code
        message
      }
    }
  }
`;

export const REJECT_COMPANY_INVITATION: TypedDocumentNode<
  { rejectCompanyInvitation: InvitationPayload },
  { input: { token: string } }
> = gql`
  mutation RejectCompanyInvitation($input: RejectCompanyInvitationInput!) {
    rejectCompanyInvitation(input: $input) {
      companyInvitation {
        ...CompanyInvitationFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

/**
 * Redeems an invitation the user DISCOVERED in-app (Invitation Discovery). No raw token — the server
 * binds the caller to the invitation by their verified email. Creates + activates the membership.
 */
export const ACCEPT_COMPANY_INVITATION_BY_ID: TypedDocumentNode<
  {
    acceptCompanyInvitationById: {
      companyUser: { id: string; companyId: string; role: string; status: string } | null;
      errors: UserError[];
    };
  },
  { input: { invitationId: string } }
> = gql`
  mutation AcceptCompanyInvitationById($input: AcceptCompanyInvitationByIdInput!) {
    acceptCompanyInvitationById(input: $input) {
      companyUser {
        id
        companyId
        role
        status
      }
      errors {
        code
        message
      }
    }
  }
`;

/** Declines a discovered invitation by id (Invitation Discovery). */
export const REJECT_COMPANY_INVITATION_BY_ID: TypedDocumentNode<
  { rejectCompanyInvitationById: InvitationPayload },
  { input: { invitationId: string } }
> = gql`
  mutation RejectCompanyInvitationById($input: RejectCompanyInvitationByIdInput!) {
    rejectCompanyInvitationById(input: $input) {
      companyInvitation {
        ...CompanyInvitationFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

export const UPDATE_COMPANY_SETTINGS: TypedDocumentNode<
  {
    updateCompanySettings: {
      companySettings: CompanySettings | null;
      errors: UserError[];
    };
  },
  { input: UpdateCompanySettingsInput }
> = gql`
  mutation UpdateCompanySettings($input: UpdateCompanySettingsInput!) {
    updateCompanySettings(input: $input) {
      companySettings {
        companyId
        corporateEmailOnly
        allowExternalUsers
        invitationExpiryDays
        allowedDomains
      }
      errors {
        code
        message
      }
    }
  }
`;

export const UPDATE_COMPANY_PROFILE: TypedDocumentNode<
  {
    updateCompanyProfile: {
      company: CompanyProfile | null;
      errors: UserError[];
    };
  },
  { input: UpdateCompanyProfileInput }
> = gql`
  mutation UpdateCompanyProfile($input: UpdateCompanyProfileInput!) {
    updateCompanyProfile(input: $input) {
      company {
        ...CompanyProfileFields
      }
      errors {
        code
        message
      }
    }
  }
  ${COMPANY_PROFILE_FIELDS}
`;
